import kebabCase from 'lodash/kebabCase';
import React from 'react';
import { type SvgIconProps } from '@mui/material/SvgIcon';

import { operationsLinks } from '../../../../routes/Operations/OperationsHomePage';
import { financialLinks } from '../../../../routes/Financial/FinancialHomePage';
import { setupLinks } from '../../../../routes/Setup/SetupHomePage';
import { settingsLinks } from '../../../../routes/Setup/General/GeneralSettings/SettingsHomePage';
import { serviceTypeEnum } from '../../../../api/serviceTypes/types';
import { strToLinkObj } from '../../../../utils/stringUtils';
import { REPORT_CATEGORIES } from '../../../../routes/Reports/ReportsHomePage/constants';

// Icons on the five top-level sections only. Deeper levels stay text — with no
// collapsed rail there is nothing for a per-item icon to earn, and sixty of
// them would be sixty more things to keep meaningful.
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';

export type NavItem = {
    permissionId?: string;
    permissionIdAny?: string[];
    label: string | undefined;
    path: string;
    notDeveloped: boolean;
    content?: NavItem[];
    icon?: React.ElementType<SvgIconProps>;
};

// CRM item names that should be moved from "General" into the "CRM" category
const crmItemNames = ['CRM Actions', 'CRM Types', 'Email Engagements', 'Email Templates'];

const createSubNavItems = (basePath: string, routesArray: any[]): NavItem[] => {
    return routesArray.map((route) => ({
        label: route.title,
        path: `${basePath}/${route.link}`,
        notDeveloped: route.notDeveloped || false,
        permissionId: route.permissionId || ''
    }));
};

const getNavItems = () => {
    // ─── 1. RESERVATION ───
    const reservationsNavItems: NavItem[] = [];
    for (const value of Object.values(serviceTypeEnum)) {
        if (value === 'Tour') {
            reservationsNavItems.push({
                label: 'Tour',
                path: '/reservations/tour',
                notDeveloped: false,
                content: [
                    {
                        label: 'Travel File List',
                        path: '/reservations/tour/travel-file-list',
                        notDeveloped: false,
                        permissionId: 'travel_file:view'
                    },
                    {
                        label: 'Tour Booking',
                        path: '/reservations/tour/manage-tour',
                        notDeveloped: false,
                        permissionId: 'tour:view'
                    }
                ]
            });
        } else {
            reservationsNavItems.push({
                label: value,
                path: `/${strToLinkObj(value, 'reservations').path}`,
                notDeveloped: true
            });
        }
    }
    const reservation: NavItem = {
        label: 'Reservation',
        icon: FlightTakeoffIcon,
        path: '/reservations',
        notDeveloped: false,
        content: reservationsNavItems
    };

    // ─── 2. FINANCE ───
    const finance: NavItem = {
        label: 'Finance',
        icon: AccountBalanceIcon,
        path: '/finance',
        notDeveloped: false,
        content: createSubNavItems('/finance', financialLinks)
    } satisfies NavItem;

    // ─── 4. REPORTS ───
    const reports: NavItem = {
        label: 'Reports',
        icon: AssessmentIcon,
        path: '/reports',
        notDeveloped: false,
        content: REPORT_CATEGORIES.map(({ title, items }) => ({
            label: title,
            path: `/reports/${kebabCase(title)}`,
            notDeveloped: false,
            content: createSubNavItems(`/reports/${kebabCase(title)}`, items)
        }))
    } satisfies NavItem;

    // ─── 5. ADMINISTRATION ───

    // 5a. Operations — top-level category
    const operations: NavItem = {
        label: 'Operations',
        icon: BuildCircleIcon,
        path: '/administration/operations',
        notDeveloped: false,
        content: [
            ...createSubNavItems(
                '/administration/operations',
                operationsLinks.filter((link) => link.title !== 'Escorted Tours')
            ),
            {
                label: 'Escorted Tours',
                path: '/operations/self-operation-tours',
                notDeveloped: false,
                // The route is guarded on this key; without it here the link
                // still showed to everyone and led straight to a refusal.
                permissionId: 'setup.escorted_tour:view'
            }
        ]
    } satisfies NavItem;

    // 5b. Users & Roles — sub-category under Administration
    const adminColumn = setupLinks.find((col) => col.title === 'Admin');

    const usersRolesChildren: NavItem[] = adminColumn
        ? adminColumn.links.map((link: any) => ({
              label: link.text,
              path: `/administration/setup/admin/${link.path}`,
              notDeveloped: false,
              permissionId: link.permissionId || '',
              permissionIdAny: link.permissionIdAny
          }))
        : [];

    // 5c. Settings — promoted from Setup → General → Settings
    const settingsNavItems: NavItem[] = settingsLinks.map((link: any) => ({
        label: link.title,
        path: `/administration/setup/general/settings/${link.link}`,
        notDeveloped: link.notDeveloped || false,
        permissionId: link.permissionId || ''
    }));

    // 5d. Database — promoted from Setup
    const databaseColumn = (setupLinks as any[]).find((col: any) => col.title === 'Database');
    const databaseNavItems: NavItem[] = databaseColumn
        ? (databaseColumn.links as any[]).map((link: any) => ({
              label: link.text,
              path: `/administration/setup/database/${link.path}`,
              notDeveloped: false,
              permissionId: link.permissionId || ''
          }))
        : [];

    // Ticket board — triage for the caller's own tenant (the super user additionally sees all tenants).
    databaseNavItems.push({
        label: 'Tickets',
        path: '/administration/setup/tickets',
        notDeveloped: false,
        permissionId: 'system.tickets:view'
    });

    // 5e. Master Data — replaces "Setup" (minus Admin and Database which are now separate)
    const masterDataCategories = setupLinks.filter((col) => !['Admin', 'Database'].includes(col.title));

    const masterDataNavItems: NavItem[] = masterDataCategories.flatMap((category: any) => {
        const links = category.links as any[];
        const base = kebabCase(category.title);

        // Split "General" into "Geography" and merge CRM items into the CRM category
        if (category.title === 'General') {
            const geographyLinks = links.filter(
                (link) => link.text !== 'Settings' && !crmItemNames.includes(link.text)
            );
            return [
                {
                    label: 'Geography',
                    path: `/administration/setup/${base}`,
                    notDeveloped: false,
                    content: geographyLinks.map((link: any) => ({
                        label: link.text,
                        path: `/administration/setup/${base}/${link.path}`,
                        notDeveloped: false,
                        permissionId: link.permissionId || ''
                    }))
                }
            ];
        }

        // Merge CRM items from "General" into the existing CRM category
        if (category.title === 'CRM') {
            const generalCategory = (setupLinks as any[]).find((col: any) => col.title === 'General');
            const crmFromGeneral = generalCategory
                ? (generalCategory.links as any[]).filter((link: any) => crmItemNames.includes(link.text))
                : [];
            // CRM items from General keep their original /general/ path
            const crmFromGeneralNav: NavItem[] = crmFromGeneral.map((link: any) => ({
                label: link.text,
                path: `/administration/setup/general/${link.path}`,
                notDeveloped: false,
                permissionId: link.permissionId || ''
            }));
            // Native CRM items use /crm/ path
            const nativeCrmNav: NavItem[] = links.map((link: any) => ({
                label: link.text,
                path: `/administration/setup/${base}/${link.path}`,
                notDeveloped: false,
                permissionId: link.permissionId || ''
            }));
            return [
                {
                    label: category.sidebarLabel || category.title,
                    path: `/administration/setup/${base}`,
                    notDeveloped: false,
                    content: [...crmFromGeneralNav, ...nativeCrmNav]
                }
            ];
        }

        return [
            {
                label: category.sidebarLabel || category.title,
                path: `/administration/setup/${base}`,
                notDeveloped: false,
                content: links.map((link: any) => ({
                    label: link.text,
                    path: `/administration/setup/${base}/${link.path}`,
                    notDeveloped: false,
                    permissionId: link.permissionId || ''
                }))
            }
        ];
    });

    const administration: NavItem = {
        label: 'Administration',
        icon: SettingsIcon,
        path: '/administration',
        notDeveloped: false,
        content: [
            {
                label: 'Users & Roles',
                path: '/administration/setup/admin',
                notDeveloped: false,
                content: usersRolesChildren
            },
            {
                label: 'Settings',
                path: '/administration/setup/general/settings',
                notDeveloped: false,
                content: settingsNavItems
            },
            {
                label: 'System',
                path: '/administration/setup/database',
                notDeveloped: false,
                content: databaseNavItems
            },
            {
                label: 'Master Data',
                path: '/administration/setup',
                notDeveloped: false,
                content: masterDataNavItems
            }
        ]
    };

    // ─── ASSEMBLE NAV ───
    return [reservation, operations, finance, reports, administration];
};

export default getNavItems;
